import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PrinterIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { settingService } from '../../services/settingService';

interface InvoiceItem {
  invoice_item_id: number;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface SaleItem {
  sale_item_id: number;
  quantity: number;
  unit_price: number;
  line_total: number;
  products?: {
    product_name: string;
    sku?: string;
  } | null;
}

interface PaymentAllocation {
  payment_allocation_id: number;
  amount: number;
  payments?: {
    payment_number: string;
    payment_date: string;
    payment_method?: string;
  } | null;
}

interface Invoice {
  invoice_id: number;
  invoice_number: string;
  invoice_date: string;
  issue_date?: string;
  due_date: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  balance_due?: number;
  paid_amount?: number;
  status: string;
  notes?: string | null;
  customers: {
    company_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
  } | null;
  sales?: {
    sale_number?: string;
    sale_items?: SaleItem[];
  } | null;
  invoice_items?: InvoiceItem[];
  payment_allocations?: PaymentAllocation[];
}

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice;
}

interface CompanySettings {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  logo?: string;
  tax_id?: string;
}

export const InvoiceModal = ({ isOpen, onClose, invoice }: InvoiceModalProps) => {
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: 'Aperture Camera Store',
    address: 'Nairobi, Kenya',
    phone: '+254 700 000 000',
    email: 'info@aperture.com',
  });

  useEffect(() => {
    if (isOpen) {
      fetchCompanySettings();
    }
  }, [isOpen]);

  const fetchCompanySettings = async () => {
    try {
      const response = await settingService.getCompanySettings();
      if (response.data?.data) {
        setCompanySettings({
          name: response.data.data.name || companySettings.name,
          address: response.data.data.address || companySettings.address,
          phone: response.data.data.phone || companySettings.phone,
          email: response.data.data.email || companySettings.email,
          logo: response.data.data.logo,
          tax_id: response.data.data.tax_id,
        });
      }
    } catch {
      // Use defaults
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getCustomerName = () => {
    if (invoice.customers) {
      return invoice.customers.company_name ||
        `${invoice.customers.first_name || ''} ${invoice.customers.last_name || ''}`.trim();
    }
    return 'Walk-in Customer';
  };

  const getCustomerAddress = () => {
    return invoice.customers?.address || '';
  };

  const getCustomerEmail = () => {
    return invoice.customers?.email || '';
  };

  const getCustomerPhone = () => {
    return invoice.customers?.phone || '';
  };

  const getItems = (): Array<{
    description: string;
    quantity: number;
    unit_price: number;
    line_total: number;
    sku?: string;
  }> => {
    if (invoice.invoice_items && invoice.invoice_items.length > 0) {
      return invoice.invoice_items.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        line_total: item.line_total,
      }));
    }
    if (invoice.sales?.sale_items && invoice.sales.sale_items.length > 0) {
      return invoice.sales.sale_items.map((item) => ({
        description: item.products?.product_name || 'Unknown Item',
        quantity: item.quantity,
        unit_price: item.unit_price,
        line_total: item.line_total,
        sku: item.products?.sku,
      }));
    }
    return [];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'text-green-600';
      case 'sent': return 'text-blue-600';
      case 'pending': return 'text-yellow-600';
      case 'overdue': return 'text-red-600';
      case 'partially_paid': return 'text-orange-600';
      case 'cancelled': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  if (!isOpen) return null;

  const items = getItems();
  const issueDate = invoice.issue_date || invoice.invoice_date;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 print:hidden">
              <h2 className="text-lg font-semibold text-gray-900">Invoice Preview</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Invoice Document */}
            <div className="p-8 overflow-auto max-h-[70vh] print:max-h-none print:overflow-visible">
              {/* A4 Document */}
              <div
                className="mx-auto bg-white print:shadow-none"
                style={{
                  width: '210mm',
                  minHeight: '297mm',
                  padding: '20mm',
                  boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                }}
              >
                {/* Company Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    {companySettings.logo && (
                      <img
                        src={companySettings.logo}
                        alt="Company Logo"
                        className="h-16 object-contain mb-2"
                      />
                    )}
                    <h1 className="text-2xl font-bold text-gray-900">{companySettings.name}</h1>
                    <div className="text-sm text-gray-600 mt-1 space-y-1">
                      {companySettings.address && (
                        <div className="flex items-center gap-1">
                          <MapPinIcon className="w-4 h-4" />
                          {companySettings.address}
                        </div>
                      )}
                      {companySettings.phone && (
                        <div className="flex items-center gap-1">
                          <PhoneIcon className="w-4 h-4" />
                          {companySettings.phone}
                        </div>
                      )}
                      {companySettings.email && (
                        <div className="flex items-center gap-1">
                          <EnvelopeIcon className="w-4 h-4" />
                          {companySettings.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <h2 className="text-3xl font-bold text-gray-400 uppercase tracking-wider">
                      Invoice
                    </h2>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {invoice.invoice_number}
                    </p>
                  </div>
                </div>

                {/* Bill To / Invoice Info */}
                <div className="flex justify-between mb-8">
                  <div className="w-1/2 pr-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To:</h3>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="font-semibold text-gray-900">{getCustomerName()}</p>
                      {getCustomerAddress() && (
                        <p className="text-gray-600 mt-1">{getCustomerAddress()}</p>
                      )}
                      {getCustomerEmail() && (
                        <p className="text-gray-600 mt-1">{getCustomerEmail()}</p>
                      )}
                      {getCustomerPhone() && (
                        <p className="text-gray-600 mt-1">{getCustomerPhone()}</p>
                      )}
                    </div>
                  </div>
                  <div className="w-1/2 pl-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                      Invoice Details:
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Issue Date:</span>
                        <span className="font-medium">
                          {issueDate ? new Date(issueDate).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      {/* <div className="flex justify-between">
                        <span className="text-gray-600">Due Date:</span>
                        <span className="font-medium">
                          {invoice.due_date
                            ? new Date(invoice.due_date).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div> */}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium capitalize ${getStatusColor(invoice.status)}`}>
                          {invoice.status.replace('_', ' ')}
                        </span>
                      </div>
                      {invoice.sales?.sale_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sale Ref:</span>
                          <span className="font-medium">{invoice.sales.sale_number}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <table className="w-full mb-8">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        #
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="py-3 px-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td className="py-4 px-4 text-sm text-gray-900">{index + 1}</td>
                        <td className="py-4 px-4 text-sm text-gray-900">
                          {item.description}
                          {item.sku && (
                            <span className="text-gray-500 block text-xs">
                              SKU: {item.sku}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-900 text-center">
                          {item.quantity}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-900 text-right">
                          KSh {Number(item.unit_price).toLocaleString()}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-900 text-right font-medium">
                          KSh {Number(item.line_total).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 px-4 text-sm text-gray-500 text-center">
                          No items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-8">
                  <div className="w-1/2 space-y-2">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">
                        KSh {Number(invoice.subtotal).toLocaleString()}
                      </span>
                    </div>
                    {Number(invoice.tax_amount) > 0 && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Tax:</span>
                        <span className="font-medium">
                          KSh {Number(invoice.tax_amount).toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 border-t-2 border-gray-900">
                      <span className="text-lg font-bold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-gray-900">
                        KSh {Number(invoice.total_amount).toLocaleString()}
                      </span>
                    </div>
                    {Number(invoice.paid_amount) > 0 && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Paid:</span>
                        <span className="font-medium text-green-600">
                          KSh {Number(invoice.paid_amount).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {Number(invoice.balance_due) > 0 && (
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Balance Due:</span>
                        <span className="font-medium text-red-600">
                          KSh {Number(invoice.balance_due).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment History */}
                {invoice.payment_allocations && invoice.payment_allocations.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Payment History:</h3>
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="py-2 px-4 text-left text-xs font-semibold text-gray-600 uppercase">
                            Date
                          </th>
                          <th className="py-2 px-4 text-left text-xs font-semibold text-gray-600 uppercase">
                            Reference
                          </th>
                          <th className="py-2 px-4 text-right text-xs font-semibold text-gray-600 uppercase">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {invoice.payment_allocations.map((alloc) => (
                          <tr key={alloc.payment_allocation_id}>
                            <td className="py-2 px-4 text-sm text-gray-900">
                              {alloc.payments?.payment_date
                                ? new Date(alloc.payments.payment_date).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-900">
                              {alloc.payments?.payment_number || 'N/A'}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-900 text-right font-medium">
                              KSh {Number(alloc.amount).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Notes */}
                {invoice.notes && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notes:</h3>
                    <p className="text-gray-600 whitespace-pre-wrap">{invoice.notes}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-500">
                    Thank you for your business!
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 print:hidden">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <PrinterIcon className="w-5 h-5" />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
};
